/**
 * Renders a bunch of icons like a stack of coins viewed from the top
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo, isValidElement } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import nanoid from 'nanoid'
import omit from 'lodash/omit'
import { multiply } from 'client/shared/js/utils/cssMath'
import styles from './StackedIcons.module.scss'

const LIMIT_ICONS_COUNT = 5
const SIZE_DEFAULT = '42px'
// The multiplier for the `spacing` mixin
const SPACING_MULTIPLIER = styles.spacing
const SPACING_OPTS = {
  'compact':  '-20px',
  'comfortable': '-10px',
  'default': '3px',
  'cozy': '10px'
}
const SPACING_OPTS_KEYS = Object.keys(SPACING_OPTS)

const propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  height: PropTypes.string,
  icons: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(PropTypes.node)
  ]),
  isReverse: PropTypes.bool,
  length: PropTypes.number,
  limit: PropTypes.number,
  onClick: PropTypes.func,
  size: PropTypes.string,
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.oneOf(SPACING_OPTS_KEYS)
  ]),
  width: PropTypes.string
}

const defaultProps = {
  title: '',
  className: '',
  color: 'rgba(255, 255, 255, 0)',
  icons: [],
  isReverse: false,
  limit: LIMIT_ICONS_COUNT,
  size: SIZE_DEFAULT,
  spacing: SPACING_OPTS.default
}

/**
 * Renders a bunch of icons like a stack of coins viewed from the top.
 * You can pass `icons` prop as an array of components and/or objects. An object may
 * contain keys to override the default props
 * @param {String} props.className The CSS class name
 * @param {String} props.height The height of the icons (will override size prop)
 * @param {Array} props.icons The array of object containing component and other props
 * @param {Boolean} props.isReverse Set true to reverse the z-index order
 * @param {Number} props.length The length used to calculate the counter. Defaults to `icons.length`
 * @param {Number} props.limit The max number of icons to display before the counter
 * @param {Function} props.onClick The onClick handler
 * @param {String} props.size The size of the icons
 * @param {String | Number} props.spacing The spacing between icons
 * @param {String} props.title The hover title
 * @param {String} props.width The width of the icons (will override size prop)
 */
const StackedIcons = ({ className, height, icons, isReverse, length, limit, onClick, size, spacing, title, width }) => {
  const components = useMemo(() => icons.map((icon, i) => {
    let component = icon
    let iconProps = Object.assign({}, { onClick, spacing })

    // If icon is an object, extract relevant keys
    if (!isValidElement(icon) && typeof icon === 'object') {
      component = icon.component
      iconProps = Object.assign(iconProps, icon)
    }

    // Override props by priority
    const key = iconProps.key || nanoid()
    const w = iconProps.width || iconProps.size || width || size
    const h = iconProps.height || iconProps.size || height || size

    const iconStyle = Object.assign({}, iconProps.style, {
      width: w,
      height: h,
      // Don't apply margin on last element
      marginRight: (i + 1 === icons.length) ? 0 : convertSpacing(spacing),
      // Create a new stacking context for proper overlapping effect
      position: 'relative',
      zIndex: isReverse ? icons.length - i : i
    })

    iconProps = omit(iconProps, ['component', 'key', 'width', 'height', 'styles'])

    return (
      <div
        key={`stacked-icon-${key}`}
        style={iconStyle}
        {...iconProps}>
        {component}
      </div>
    )
  }), [height, icons, isReverse, onClick, size, spacing, width])

  return (
    <div title={title} className={cx(styles['stacked-icons'], className)}>
      {components}
    </div>
  )
}

StackedIcons.propTypes = propTypes

StackedIcons.defaultProps = defaultProps

/**
 * If valid opts name, return respective value. If number, multiply it by `spacing`
 * mixin value. If else, return itself.
 * @param {String | Number} value The spacing value
 */
const convertSpacing = (value) => {
  if (SPACING_OPTS_KEYS.includes(value)) {
    return SPACING_OPTS[value]
  }

  if (typeof value === 'number') {
    return calcSpacing(value)
  }

  return value
}

/**
 * Mimics `spacing` mixin defined in the Sass file
 * @param {String | Number} str The spacing value
 */
const calcSpacing = (str) => {
  return multiply(str, SPACING_MULTIPLIER)
}

export default StackedIcons
